import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { authAPI } from '@/services/api';
import { Check, Eye, EyeOff } from 'lucide-react';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('Lykilorð stemma ekki');
      return;
    }
    if (newPassword.length < 6) {
      setError('Lykilorð verður að vera að minnsta kosti 6 stafir');
      return;
    }
    setIsLoading(true);
    try {
      await authAPI.resetPassword({ token, new_password: newPassword });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Villa við endurstillingu');
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-background">
        <div className="card p-6 text-center max-w-sm">
          <p className="text-error mb-4">Ógildur hlekkur</p>
          <Link to="/login" className="btn btn-primary">Til baka í innskráningu</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="w-full max-w-sm">
        <h2 className="text-2xl font-semibold mb-2">Nýtt lykilorð</h2>
        <p className="text-foreground-secondary text-sm mb-6">Veldu nýtt lykilorð fyrir aðganginn þinn.</p>

        {success ? (
          <div className="card p-6 text-center space-y-3" data-testid="reset-success">
            <Check size={32} className="mx-auto text-accent" />
            <p className="font-semibold">Lykilorð hefur verið endurstillt</p>
            <Link to="/login" className="btn btn-primary inline-flex items-center gap-2 mt-4" data-testid="back-to-login-btn">
              Skrá inn
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-password-form">
            {error && (
              <div className="p-3 bg-error/10 border border-error/20 rounded-lg text-error text-sm">{error}</div>
            )}
            <div>
              <label className="block text-sm font-medium text-foreground-secondary mb-2">Nýtt lykilorð</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  data-testid="reset-new-password"
                  className="pr-11"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-muted hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-secondary mb-2">Staðfesta lykilorð</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="reset-confirm-password"
              />
            </div>
            <button type="submit" disabled={isLoading} className="w-full btn btn-primary btn-lg" data-testid="reset-submit">
              {isLoading ? <div className="spinner w-5 h-5" /> : 'Endurstilla lykilorð'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
